<template>
  <v-row class="mt-2">
    <v-col class="py-0" cols="12">
      <v-alert tile type="error" v-if="error">{{ error }}</v-alert>
    </v-col>

    <v-col cols="12" md="2" sm="6">
      <v-text-field
        @click:append-outer="refresh()"
        @click:clear="clearSearch()"
        @keydown.enter="refresh()"
        clearable
        dense
        hide-details
        outlined
        required
        :label="$t('datatable.searchLabel')"
        v-model="search"
      ></v-text-field>
    </v-col>

    <v-spacer />

    <v-col cols="12" md="2" align="right">
      <v-btn @click="resetFilters()" color="primary" dark rounded small text>
        {{ $t("label.clearFilters") }}
      </v-btn>
    </v-col>

    <v-col cols="12" md="12">
      <v-data-table
        :expanded.sync="expanded"
        :footer-props="{
          'disable-pagination': $apollo.queries.allTaskResultExtensions.loading,
          'items-per-page-options': [10, 25, 50, 75, 100],
        }"
        :headers="headers"
        :items="allTaskResultExtensions.edges"
        :loading="$apollo.queries.allTaskResultExtensions.loading"
        :options.sync="dataTableOptions"
        :server-items-length="allTaskResultExtensions.totalCount"
        @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
        class="row-pointer"
        item-key="node.id"
        single-expands
        show-expand
      >
        <template v-slot:[`item.node.pk`]="{ item }">
          #TASK{{ item.node.pk }}
        </template>
        <template v-slot:[`item.node.createdAt`]="{ item }">
          {{ item.node.createdAt | moment("YYYY-MM-DD HH:mm") }}
        </template>
        <template v-slot:[`item.node.taskResult.status`]="{ item }">
          <v-chip
            :color="
              item.node.taskResult?.status == 'STARTED'
                ? 'info'
                : item.node.taskResult?.status == 'SUCCESS'
                ? 'success'
                : item.node.taskResult?.status == 'FAILURE'
                ? 'error'
                : ''
            "
            small
          >
            <v-progress-circular
              :size="14"
              :width="1"
              class="ml-n1 mr-1"
              color="white"
              indeterminate
              v-if="item.node.taskResult?.status == 'STARTED'"
            ></v-progress-circular>
            <v-icon left small v-else>
              {{
                item.node.taskResult?.status == "SUCCESS"
                  ? "check_circle"
                  : item.node.taskResult?.status == "FAILURE"
                  ? "cancel"
                  : ""
              }}
            </v-icon>
            {{ item.node.taskResult?.status }}
          </v-chip>
        </template>

        <template v-slot:item.data-table-expand="{ item, isExpanded }">
          <td
            v-if="item.node.taskResult?.status == 'SUCCESS'"
            class="text-start"
          >
            <v-btn
              icon
              class="v-data-table__expand-icon"
              :class="{
                'v-data-table__expand-icon--active': isExpanded,
              }"
            >
              <v-icon>expand_more</v-icon>
            </v-btn>
          </td>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <v-data-table
              :headers="headersFile"
              :hide-default-footer="true"
              :hide-default-header="true"
              :items-per-page="100"
              :items="item.node.taskresultextensionfileSet.edges"
              @click:row="(item) => fetchTaskResultExtensionFile(item.node.id)"
              class="row-pointer"
              item-key="item.node.id"
            >
              <template v-slot:[`item.action`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      :color="item.node.isDownloaded ? 'success' : 'error'"
                      v-on="on"
                      >{{
                        item.node.isDownloaded ? "check_circle" : "cancel"
                      }}</v-icon
                    >
                  </template>
                  <span>
                    {{ $t("label.isDownloaded") }}
                  </span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon color="primary">
                      <v-icon>file_download</v-icon>
                    </v-btn>
                  </template>
                  <span> Download </span>
                </v-tooltip>
              </template>
            </v-data-table>
          </td>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";

export default {
  name: "exports-list",
  components: {},

  apollo: {
    allTaskResultExtensions: {
      query: gql`
        query allTaskResultExtensions(
          $first: Int
          $last: Int
          $before: String
          $after: String
          $orderBy: [String]
          $search: String
          $createdBy: ID!
          $type: TaskResultExtensionType!
        ) {
          allTaskResultExtensions(
            first: $first
            last: $last
            before: $before
            after: $after
            orderBy: $orderBy
            search: $search
            createdBy: $createdBy
            type: $type
          ) {
            edgeCount
            totalCount
            pageInfo {
              startCursor
              endCursor
              hasPreviousPage
              hasNextPage
            }
            edges {
              node {
                id
                createdAt
                name
                pk
                taskResult {
                  status
                }
                taskresultextensionfileSet {
                  edges {
                    node {
                      id
                      isDownloaded
                      name
                    }
                  }
                }
              }
            }
          }
        }
      `,
      fetchPolicy: "cache-and-network",
      update: (data) => data.allTaskResultExtensions,
      skip: true,
      pollInterval: 30000,
    },

    taskResultExtensionFile: {
      query: gql`
        query taskResultExtensionFile($id: ID!) {
          taskResultExtensionFile(id: $id) {
            id
            filePath
            name
          }
        }
      `,
      fetchPolicy: "network-only",
      update: (data) => data.taskResultExtensionFile,
      skip: true,
      error(error) {
        console.error("We've got an error!", error);
      },
    },
  },

  data: function () {
    return {
      allTaskResultExtensions: {},
      error: null,
      expanded: [],
      taskResultExtensionFile: {},

      headers: [
        {
          text: this.$tc("label.pk", 1),
          value: "node.pk",
          sortable: false,
        },
        {
          text: this.$t("label.created"),
          value: "node.createdAt",
          sortable: true,
        },
        {
          text: this.$tc("label.name", 1),
          value: "node.name",
          sortable: true,
        },
        {
          text: this.$t("label.status"),
          value: "node.taskResult.status",
          sortable: false,
        },
        { text: "", value: "data-table-expand" },
      ],
      headersFile: [
        {
          text: this.$tc("label.name", 1),
          value: "node.name",
          sortable: false,
        },
        {
          text: this.$t("label.action"),
          value: "action",
          align: "right",
          sortable: false,
        },
      ],
    };
  },

  computed: {
    me() {
      return this.$store.state.user.me;
    },

    dataTableOptions: {
      get() {
        return this.$store.getters["exportsList/getDataTableOptions"];
      },
      set(value) {
        this.$store.dispatch("exportsList/setDataTableOptions", value);
      },
    },

    search: {
      get() {
        return this.$store.getters["exportsList/getSearch"];
      },
      set(value) {
        this.$store.dispatch("exportsList/setSearch", value);
      },
    },
  },

  watch: {
    dataTableOptions() {
      this.refresh();
    },

    taskResultExtensionFile(val) {
      if (val) {
        this.updateTaskResultExtensionFile(val.id);
        this.downloadDocument(val.name, val.filePath);
      }
    },
  },

  created() {
    this.orderByList = helper.orderByList;
  },

  mounted() {},

  methods: {
    clearSearch() {
      this.search = null;
      this.refresh();
    },

    async downloadDocument(fileName, filePath) {
      try {
        const response = await fetch(filePath);
        const blob = await response.blob();
        const url = await URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        this.error = err;
        console.log({ err });
      }
    },

    fetchTaskResultExtensionFile(id) {
      this.$apollo.queries.taskResultExtensionFile.setVariables({
        id: id,
      });

      this.$apollo.queries.taskResultExtensionFile.skip = false;
      this.$apollo.queries.taskResultExtensionFile.refresh();
    },

    refresh() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.dataTableOptions;

      var _first = itemsPerPage;
      var _last = null;
      var _before = null;
      var _after = null;

      if (page < this.page) {
        _first = null;
        _last = itemsPerPage;
        _before = this.allTaskResultExtensions.pageInfo.startCursor;
        _after = null;
      }

      if (page > this.page) {
        _first = itemsPerPage;
        _last = null;
        _before = null;
        _after = this.allTaskResultExtensions.pageInfo.endCursor;
      }

      var orderByList = this.orderByList(sortBy, sortDesc);

      this.$apollo.queries.allTaskResultExtensions.setVariables({
        first: _first,
        last: _last,
        before: _before,
        after: _after,
        orderBy: orderByList,
        search: this.search,
        createdBy: this.me.id,
        type: "PBI_REPORT_EXPORT",
      });

      this.$apollo.queries.allTaskResultExtensions.skip = false;
      this.$apollo.queries.allTaskResultExtensions.refresh();

      this.page = page;
    },

    resetFilters() {
      this.clearSearch();
    },

    updateTaskResultExtensionFile(id) {
      this.error = null;

      var payload = {
        id: id,
        isDownloaded: true,
      };

      this.$apollo
        .mutate({
          mutation: gql`
            mutation updateTaskResultExtensionFile(
              $input: UpdateTaskResultExtensionFileInput!
            ) {
              updateTaskResultExtensionFile(input: $input) {
                taskResultExtensionFile {
                  id
                  isDownloaded
                }
              }
            }
          `,
          variables: {
            input: payload,
          },
        })
        .then((response) => {
          // this.$apollo.queries.allTaskResultExtensions.refresh();

          this.$store
            .dispatch("tasks/fetchTaskResultExtensionFileStatus")
            .then(() => {})
            .catch((error) => {
              console.log(error);
            });
        })
        .catch((error) => {
          this.error = error.graphQLErrors[0].message;

          const payload = {
            color: "error",
            message: this.error,
          };
          this.$store.dispatch("snackbar/showMessage", payload);
        })
        .finally(() => {});
    },
  },
};
</script>

<style>
.row-pointer > .v-data-table__wrapper > table > tbody > tr:hover {
  cursor: pointer;
}
</style>

<style scoped>
.v-data-table
  >>> .v-data-table__wrapper
  tbody
  tr.v-data-table__expanded__content {
  box-shadow: none;
}
</style>
